import { useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { CalendarDays, ChevronLeft, ChevronRight, DoorOpen, Settings, Link2, AlertTriangle, Clock, User, Mail, Users, StickyNote, X, UserCheck, Phone, Ticket, RotateCcw, Star, Search } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Header from "../components/dashboard/Header";

const DAY_START = 7 * 60;   // 07:00
const DAY_END = 21 * 60;    // 21:00
const SPAN = DAY_END - DAY_START;

const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const shiftDay = (iso, delta) => {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(y, m - 1, d + delta);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
};
const prettyDate = (iso) => {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });
};
// Wall-clock minutes-from-midnight from an event's own ISO (room-local offset)
const wallMinutes = (iso, fallback) => {
  if (!iso) return fallback;
  const t = iso.split("T")[1];
  if (!t) return fallback;
  const [h, mn] = t.slice(0, 5).split(":").map(Number);
  return h * 60 + mn;
};
const hhmm = (min) => {
  const h = Math.floor(min / 60), m = min % 60;
  const ap = h >= 12 ? "pm" : "am";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}${m ? ":" + String(m).padStart(2, "0") : ""}${ap}`;
};

function RoomRow({ room, onSelect }) {
  const blocks = useMemo(() => (room.events || []).map((e) => {
    const s = e.all_day ? DAY_START : wallMinutes(e.start, DAY_START);
    const en = e.all_day ? DAY_END : wallMinutes(e.end, DAY_END);
    const cs = Math.max(s, DAY_START), ce = Math.min(en, DAY_END);
    return {
      ...e,
      left: `${((cs - DAY_START) / SPAN) * 100}%`,
      width: `${(Math.max(ce - cs, 8) / SPAN) * 100}%`,
      label: e.all_day ? "All day" : `${hhmm(s)}–${hhmm(en)}`,
    };
  }), [room.events]);
  const bookedCount = (room.events || []).length;
  return (
    <div className="flex items-stretch gap-3 py-2" data-testid={`room-row-${room.id}`}>
      <div className="w-44 shrink-0">
        <p className="text-sm text-brand-ink font-medium flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: room.color || "#2f6f4f" }} />
          <span className="truncate">{room.room}</span>
        </p>
        <p className="text-[11px] text-brand-tertiary mt-0.5">
          {room.error ? <span className="text-brand-neg">Unavailable</span>
            : `${room.capacity ? `seats ${room.capacity} · ` : ""}${bookedCount === 0 ? "Free all day" : `${bookedCount} booking${bookedCount > 1 ? "s" : ""}`}`}
        </p>
      </div>
      <div className="relative flex-1 h-10 rounded-md bg-brand-hover/60 border border-brand-border overflow-hidden">
        {room.error ? (
          <div className="absolute inset-0 flex items-center gap-1.5 px-2 text-[11px] text-brand-neg truncate">
            <AlertTriangle className="h-3 w-3 shrink-0" /> <span className="truncate">{room.error}</span>
          </div>
        ) : blocks.length === 0 ? (
          <div className="absolute inset-0 flex items-center justify-center text-[11px] text-brand-tertiary">Available</div>
        ) : blocks.map((b) => (
          <button key={b.id} title={`${b.summary}${b.booked_by ? " · " + b.booked_by : ""} · ${b.label}`} data-testid={`booking-${b.id}`}
            onClick={() => onSelect({ ...b, room: room.room, location: room.location })}
            className="absolute top-1 bottom-1 rounded px-1.5 flex items-center overflow-hidden text-[10px] font-medium text-white shadow-sm cursor-pointer hover:brightness-110 transition-[filter]"
            style={{ left: b.left, width: b.width, background: room.color || "#2f6f4f" }}>
            <span className="truncate">{b.summary}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function BookingCard({ booking, onClose }) {
  if (!booking) return null;
  const fmtTime = (iso) => { const t = (iso || "").split("T")[1]; if (!t) return ""; const [h, m] = t.slice(0, 5).split(":").map(Number); const ap = h >= 12 ? "pm" : "am"; return `${h % 12 === 0 ? 12 : h % 12}${m ? ":" + String(m).padStart(2, "0") : ""}${ap}`; };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="booking-card" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40" />
      <div className="relative bg-brand-surface border border-brand-border rounded-lg shadow-xl w-full max-w-sm p-6" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} data-testid="booking-card-close" className="absolute top-3 right-3 text-brand-tertiary hover:text-brand-ink"><X className="h-4 w-4" /></button>
        <p className="text-xs uppercase tracking-[0.15em] text-brand-tertiary">{booking.room} · {booking.location}</p>
        <p className="font-serif text-2xl text-brand-ink mt-1" data-testid="booking-card-company">{booking.company || booking.summary}</p>
        <div className="mt-4 space-y-3 text-sm">
          <div className="flex items-center gap-2 text-brand-ink"><Clock className="h-4 w-4 text-brand-tertiary shrink-0" /> {fmtTime(booking.start)} – {fmtTime(booking.end)}</div>
          {booking.booked_by && <div className="flex items-center gap-2 text-brand-ink"><User className="h-4 w-4 text-brand-tertiary shrink-0" /> <span data-testid="booking-card-bookedby">{booking.booked_by}</span></div>}
          {booking.email && <div className="flex items-center gap-2 text-brand-muted"><Mail className="h-4 w-4 text-brand-tertiary shrink-0" /> <a href={`mailto:${booking.email}`} className="hover:text-brand-primary truncate">{booking.email}</a></div>}
          {booking.attendees ? <div className="flex items-center gap-2 text-brand-muted"><Users className="h-4 w-4 text-brand-tertiary shrink-0" /> {booking.attendees} attendee{booking.attendees > 1 ? "s" : ""}</div> : null}
          {booking.note && <div className="flex items-start gap-2 text-brand-muted"><StickyNote className="h-4 w-4 text-brand-tertiary shrink-0 mt-0.5" /> <span className="whitespace-pre-wrap">{booking.note}</span></div>}
        </div>
        <p className="mt-4 pt-3 border-t border-brand-border text-[11px] text-brand-tertiary">{booking.booking_type ? `${booking.booking_type} booking` : "Booking"} · pulled live from Yardi Kube</p>
      </div>
    </div>
  );
}

function MapRow({ row, properties, onSaved }) {
  const [pid, setPid] = useState(row.property_id || "");
  const [busy, setBusy] = useState(false);
  const dirty = pid !== (row.property_id || "");
  const save = async () => {
    setBusy(true);
    try {
      const prop = properties.find((p) => String(p.id) === String(pid));
      const { data } = await api.put("/bookings/property-map", {
        location: row.location, property_id: pid, property_name: prop?.name || "",
      });
      toast.success(data.mapped ? "Property linked" : "Mapping cleared", { description: row.location });
      onSaved();
    } catch (err) {
      toast.error("Could not save", { description: formatApiErrorDetail(err.response?.data?.detail) });
    } finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-3 py-2" data-testid={`map-${row.location}`}>
      <div className="w-44 shrink-0">
        <p className="text-sm text-brand-ink truncate">{row.location}</p>
        {row.property_id && !dirty ? (
          <p className="text-[10px] text-brand-pos flex items-center gap-1"><Link2 className="h-3 w-3" /> {row.property_name || "linked"}</p>
        ) : <p className="text-[10px] text-brand-tertiary">not linked</p>}
      </div>
      <select value={pid} onChange={(e) => setPid(e.target.value)} data-testid={`map-select-${row.location}`}
        className="flex-1 bg-transparent border-b border-brand-borderStrong py-1.5 text-xs focus:outline-none focus:border-brand-primary text-brand-ink">
        <option value="">— not linked —</option>
        {properties.map((p) => <option key={p.id} value={p.id}>{p.name}{p.city ? ` · ${p.city}` : ""}</option>)}
      </select>
      <button onClick={save} disabled={busy || !dirty} data-testid={`map-save-${row.location}`}
        className="shrink-0 px-3 py-1.5 text-xs bg-brand-primary text-brand-page hover:bg-brand-primaryHover transition-colors disabled:opacity-40">
        {busy ? "…" : "Save"}
      </button>
    </div>
  );
}

export function ManageProperties({ reload }) {
  const [state, setState] = useState({ properties: [], locations: [], mappings: [], configured: true });
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/bookings/properties"); setState(data); }
    catch (e) {
      const s = e.response?.status;
      const msg = s === 403 ? "Only admins can link locations to Yardi properties."
        : s === 502 ? `Yardi didn't respond just now (${e.response?.data?.detail || "connection error"}). Bookings already linked keep working — refresh to retry.`
        : "Couldn't load Yardi properties — refresh to retry.";
      setState({ properties: [], locations: [], mappings: [], configured: true, error: msg });
    }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const onSaved = () => { load(); reload(); };
  const mapByLoc = useMemo(() => Object.fromEntries((state.mappings || []).map((m) => [m.location, m])), [state.mappings]);
  return (
    <div className="bg-brand-surface border border-brand-border p-6" data-testid="manage-properties">
      <p className="text-xs uppercase tracking-[0.15em] text-brand-tertiary flex items-center gap-1.5"><Settings className="h-3.5 w-3.5" /> Link locations to Yardi properties</p>
      <div className="mt-2 text-[11px] text-brand-muted leading-relaxed">
        Match each Bond location to its Yardi Kube property. Meeting-room bookings are then pulled live from Yardi for the linked property.
      </div>
      {state.error ? (
        <p className="mt-6 text-sm text-brand-neg" data-testid="yardi-props-error">{state.error}</p>
      ) : !state.configured ? (
        <p className="mt-6 text-sm text-brand-neg">Yardi API key not configured yet — add it in the backend settings to enable booking sync.</p>
      ) : loading ? (
        <div className="py-10 flex justify-center"><div className="h-6 w-6 rounded-full border-2 border-brand-border border-t-brand-primary animate-spin" /></div>
      ) : (
        <div className="mt-5 divide-y divide-brand-border/60">
          {(state.locations || []).map((loc) => (
            <MapRow key={loc} row={{ location: loc, ...(mapByLoc[loc] || {}) }} properties={state.properties} onSaved={onSaved} />
          ))}
        </div>
      )}
    </div>
  );
}

function DayPasses({ date, location, isAdmin }) {
  const [state, setState] = useState({ passes: [], configured: true, error: null });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [search, setSearch] = useState("");
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { date, location };
      if (search.trim()) params.search = search.trim();
      const { data } = await api.get("/daypasses", { params });
      setState(data);
    }
    catch (e) { setState({ passes: [], configured: true, error: "load_failed", message: e.userMessage }); }
    finally { setLoading(false); }
  }, [date, location, search]);
  useEffect(() => { const t = setTimeout(load, search ? 300 : 0); return () => clearTimeout(t); }, [load, search]);

  const act = async (p, verb) => {
    setBusy(p.id);
    try {
      await api.post(`/daypasses/${p.id}/${verb}`);
      toast.success(verb === "checkin" ? `Checked in ${p.guest_name || "guest"}` : `Checked out ${p.guest_name || "guest"}`);
      load();
    } catch (err) { toast.error("Action failed", { description: formatApiErrorDetail(err.response?.data?.detail) }); }
    finally { setBusy(null); }
  };
  const statusOf = (p) => p.checked_in ? "in" : "booked";
  const badge = { booked: "bg-brand-hover text-brand-muted border-brand-border", in: "bg-brand-pos/15 text-brand-pos border-brand-pos/30" };
  const label = { booked: "Booked", in: "Checked in" };
  const passes = state.passes || [];
  const checkedInCount = passes.filter((p) => p.checked_in && !p.checked_out_at).length;
  const searching = !!search.trim();
  const legacyTotal = state.legacy_total || 0;
  const clearLegacy = async () => {
    if (!window.confirm(`Retire ${legacyTotal} legacy Slack day pass${legacyTotal === 1 ? "" : "es"}? Yardi bookings are not affected.`)) return;
    try {
      const { data } = await api.delete("/daypasses/legacy");
      toast.success(`Retired ${data.removed} legacy day pass${data.removed === 1 ? "" : "es"}`);
      load();
    } catch (err) { toast.error("Could not clear", { description: formatApiErrorDetail(err.response?.data?.detail) }); }
  };

  const notReady = state.error && (state.error === "not_configured" || state.error === "no_mappings" || state.error.includes("channel_not_found"));
  return (
    <div className="bg-brand-surface border border-brand-border rounded-md" data-testid="daypasses-panel">
      <div className="flex flex-wrap items-center justify-between gap-2 px-5 py-3 border-b border-brand-border">
        <p className="text-sm text-brand-ink flex items-center gap-2"><Ticket className="h-4 w-4 text-brand-tertiary" /> Day Passes <span className="text-brand-tertiary">· {searching ? `${passes.length} match${passes.length === 1 ? "" : "es"}` : `${passes.length} today${checkedInCount ? ` · ${checkedInCount} in` : ""}`}</span></p>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-brand-tertiary" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="daypass-search"
              placeholder="Find guest to check in…"
              className="w-52 border border-brand-border rounded-md pl-8 pr-7 py-1.5 text-xs bg-brand-page text-brand-ink" />
            {search && <button onClick={() => setSearch("")} data-testid="daypass-search-clear" className="absolute right-2 top-1/2 -translate-y-1/2 text-brand-tertiary hover:text-brand-ink"><X className="h-3.5 w-3.5" /></button>}
          </div>
          <button onClick={load} data-testid="daypasses-refresh" className="text-brand-tertiary hover:text-brand-ink" title="Refresh"><RotateCcw className="h-3.5 w-3.5" /></button>
          {isAdmin && legacyTotal > 0 && (
            <button onClick={clearLegacy} data-testid="daypass-clear-legacy"
              title="Remove the old Slack-era day passes (Yardi bookings unaffected)"
              className="text-[11px] text-brand-neg border border-brand-neg/40 rounded px-2 py-1 hover:bg-brand-neg/10">Retire {legacyTotal} legacy</button>
          )}
        </div>
      </div>
      {loading ? (
        <div className="py-8 flex justify-center"><div className="h-5 w-5 rounded-full border-2 border-brand-border border-t-brand-primary animate-spin" /></div>
      ) : notReady ? (
        <p className="px-5 py-4 text-[12px] text-brand-muted">{state.error === "no_mappings"
          ? <>Day passes pull live from Yardi Kube. An admin needs to link each location to its Yardi property in <B>Settings → Integrations</B>.</>
          : <>Day passes pull live from Yardi Kube, but it isn't connected yet. An admin needs to add the Yardi Kube API key in the backend settings.</>}</p>
      ) : state.error === "load_failed" ? (
        <p className="px-5 py-4 text-[12px] text-brand-neg" data-testid="daypasses-error">Day passes couldn't load: {state.message} Use refresh to retry.</p>
      ) : passes.length === 0 ? (
        <p className="px-5 py-6 text-center text-sm text-brand-tertiary" data-testid="daypasses-empty">{searching ? `No day passes match “${search.trim()}”.` : `No day passes for this day${location && location !== "all" ? ` at ${location}` : ""}.`}</p>
      ) : (
        <div className="divide-y divide-brand-border/60">
          {passes.map((p) => {
            const st = statusOf(p);
            return (
              <div key={p.id} className="flex items-center gap-3 px-5 py-3" data-testid={`daypass-${p.id}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-brand-ink font-medium truncate flex items-center gap-1.5">{p.guest_name || "Guest"}
                    {p.repeat && <span title={`${p.visit_count} visits — potential member`} data-testid={`daypass-repeat-${p.id}`} className="inline-flex items-center gap-0.5 text-[10px] text-brand-gold border border-brand-gold/40 rounded-full px-1.5 shrink-0"><Star className="h-2.5 w-2.5" />{p.visit_count}</span>}
                    <span className={`text-[10px] uppercase tracking-[0.1em] px-2 py-0.5 rounded-full border shrink-0 ${badge[st]}`} data-testid={`daypass-status-${p.id}`}>{label[st]}</span>
                  </p>
                  <p className="text-[11px] text-brand-tertiary flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-0.5 min-w-0">
                    {searching && p.pass_date ? <span className="inline-flex items-center gap-1 text-brand-ink font-medium"><CalendarDays className="h-3 w-3 shrink-0" />{prettyDate(p.pass_date)}</span> : null}
                    {(location === "all" || !location) && p.location ? <span>{p.location}</span> : null}
                    {p.product ? <span>{p.product}</span> : null}
                    {p.guest_email ? <span className="inline-flex items-center gap-1 max-w-full truncate"><Mail className="h-3 w-3 shrink-0" /><span className="truncate">{p.guest_email}</span></span> : null}
                    {p.phone ? <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3 shrink-0" />{p.phone}</span> : null}
                  </p>
                  {p.checked_in && <p className="text-[10px] text-brand-tertiary mt-0.5">in {new Date(p.checked_in_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}{p.checked_in_by ? ` by ${p.checked_in_by}` : ""}</p>}
                </div>
                {st === "booked"
                  ? <button onClick={() => act(p, "checkin")} disabled={busy === p.id} data-testid={`daypass-checkin-${p.id}`} className="shrink-0 inline-flex items-center gap-1.5 text-xs bg-brand-primary text-brand-page px-3 py-1.5 rounded hover:bg-brand-primaryHover disabled:opacity-50"><UserCheck className="h-3.5 w-3.5" /> Check in</button>
                  : <span className="shrink-0 inline-flex items-center gap-1 text-[11px] text-brand-pos"><UserCheck className="h-3.5 w-3.5" /> Checked in</span>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
const B = ({ children }) => <strong className="text-brand-ink font-medium">{children}</strong>;

export default function Bookings() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [date, setDate] = useState(todayISO());
  const [location, setLocation] = useState(() => new URLSearchParams(window.location.search).get("location") || "all");
  const [config, setConfig] = useState({ configured: true, mappings: [], mapped_count: 0 });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showManage, setShowManage] = useState(false);
  const [selected, setSelected] = useState(null);

  const loadConfig = useCallback(async () => {
    try { const { data } = await api.get("/bookings/config"); setConfig(data); }
    catch (e) { toast.error("Couldn't load room setup", { description: e.userMessage, id: "bookings-config" }); }
  }, []);
  const loadEvents = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/bookings/events", { params: { date, location } });
      setData(data);
    } catch (e) { setData({ rooms: [] }); toast.error("Couldn't load room bookings", { description: e.userMessage, id: "bookings-events" }); }
    finally { setLoading(false); }
  }, [date, location]);

  useEffect(() => { loadConfig(); }, [loadConfig]);
  useEffect(() => { loadEvents(); }, [loadEvents]);

  const mappedLocations = useMemo(
    () => (config.mappings || []).filter((m) => m.property_id).map((m) => m.location).sort(),
    [config.mappings]);
  const rooms = data?.rooms || [];
  const byLocation = useMemo(() => {
    const g = {};
    rooms.forEach((r) => { (g[r.location] = g[r.location] || []).push(r); });
    return g;
  }, [rooms]);
  const notConfigured = data && data.configured === false;
  const noneMapped = config.configured && config.mapped_count === 0;

  return (
    <div className="min-h-screen bg-brand-page relative z-10">
      <Header />
      <main className="max-w-[1200px] mx-auto px-6 lg:px-12 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-brand-tertiary">Operations</p>
            <h1 className="font-serif text-4xl lg:text-5xl font-light tracking-tight text-brand-ink mt-2">Bookings</h1>
            <p className="text-sm text-brand-muted mt-1">Live conference-room availability, pulled straight from Yardi Kube.</p>
          </div>
        </div>

        {/* Controls */}
        <div className="mt-8 flex flex-wrap items-center gap-3">
          <div className="flex items-center border border-brand-border rounded-md overflow-hidden bg-brand-surface">
            <button onClick={() => setDate(shiftDay(date, -1))} data-testid="day-prev" className="p-2 hover:bg-brand-hover"><ChevronLeft className="h-4 w-4" /></button>
            <div className="px-3 flex items-center gap-2 text-sm text-brand-ink"><CalendarDays className="h-4 w-4 text-brand-tertiary" />{prettyDate(date)}</div>
            <button onClick={() => setDate(shiftDay(date, 1))} data-testid="day-next" className="p-2 hover:bg-brand-hover"><ChevronRight className="h-4 w-4" /></button>
          </div>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value || todayISO())} data-testid="date-input"
            className="border border-brand-border rounded-md px-3 py-2 text-sm bg-brand-surface text-brand-ink" />
          <button onClick={() => setDate(todayISO())} data-testid="today-btn" className="text-xs text-brand-muted hover:text-brand-ink px-2">Today</button>
          <select value={location} onChange={(e) => setLocation(e.target.value)} data-testid="location-filter"
            className="border border-brand-border rounded-md px-3 py-2 text-sm bg-brand-surface text-brand-ink">
            <option value="all">All locations</option>
            {mappedLocations.map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
        </div>

        {data?.error && (
          <div className="mt-4 bg-brand-surface border border-brand-neg/40 text-brand-neg text-sm px-4 py-3 rounded-md flex items-center gap-2" data-testid="bookings-error">
            <AlertTriangle className="h-4 w-4" /> {data.error}
          </div>
        )}

        {/* Day passes (FlexSpace) */}
        <div className="mt-6">
          <DayPasses date={date} location={location} isAdmin={isAdmin} />
        </div>

        {/* Calendar body */}
        <div className="mt-6">
          {loading ? (
            <div className="py-20 flex justify-center"><div className="h-8 w-8 rounded-full border-2 border-brand-border border-t-brand-primary animate-spin" /></div>
          ) : notConfigured ? (
            <div className="bg-brand-surface border border-brand-border p-10 text-center" data-testid="not-configured">
              <DoorOpen className="h-8 w-8 text-brand-tertiary mx-auto" />
              <p className="text-brand-ink mt-3">Yardi isn't connected yet</p>
              <p className="text-sm text-brand-muted mt-1 max-w-md mx-auto">An admin needs to add the Yardi Kube API key in the backend settings, then link each location to its Yardi property.</p>
            </div>
          ) : noneMapped ? (
            <div className="bg-brand-surface border border-brand-border p-10 text-center" data-testid="none-mapped">
              <DoorOpen className="h-8 w-8 text-brand-tertiary mx-auto" />
              <p className="text-brand-ink mt-3">No locations linked to Yardi yet</p>
              <p className="text-sm text-brand-muted mt-1 max-w-md mx-auto">
                {isAdmin ? <>An admin can link each Bond location to its Yardi property in <strong>Settings → Integrations</strong>.</> : "An admin needs to link the locations to Yardi."}
              </p>
            </div>
          ) : rooms.length === 0 ? (
            <div className="bg-brand-surface border border-brand-border p-10 text-center text-sm text-brand-muted" data-testid="no-rooms">No meeting rooms for this location.</div>
          ) : (
            Object.keys(byLocation).sort().map((loc) => {
              const locRooms = byLocation[loc];
              const freeNow = locRooms.filter((r) => !r.error && (r.events || []).length === 0).length;
              return (
                <div key={loc} className="mb-8" data-testid={`loc-group-${loc}`}>
                  <div className="flex items-center justify-between border-b border-brand-border pb-2 mb-1">
                    <p className="text-sm font-semibold text-brand-ink">{loc}</p>
                    <p className="text-[11px] text-brand-tertiary">{freeNow}/{locRooms.length} free all day</p>
                  </div>
                  {/* hour ruler */}
                  <div className="flex items-center gap-3 text-[10px] text-brand-tertiary pl-44 mt-2">
                    <div className="flex-1 flex justify-between">
                      {Array.from({ length: (DAY_END - DAY_START) / 120 + 1 }, (_, i) => DAY_START + i * 120).map((m) => (
                        <span key={m}>{hhmm(m)}</span>
                      ))}
                    </div>
                  </div>
                  <div className="divide-y divide-brand-border/60">
                    {locRooms.map((r) => <RoomRow key={r.id} room={r} onSelect={setSelected} />)}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </main>
      <BookingCard booking={selected} onClose={() => setSelected(null)} />
    </div>
  );
}
